import type {
  BidDeliverable,
  BidReviewResult,
  ContractTerm,
  CriticalDate,
  CvMatchResult,
  EvaluationCriterion,
  Requirement,
  RoleRequirement,
  TenderCase,
  TenderDocument,
} from "../types"
import type { TenderExtractionApi } from "@/api/tender-api"

import { renderStep3 } from "./build-tender-step3"
import { renderCvMatch } from "./build-tender-step3-cv"
import { pageSuffix } from "./source-page"
import { statusLabelSv } from "./tender-status-label"

type StepIndex = 1 | 2 | 3 | 4 | 5 | 6

const STEP_LABELS: Record<StepIndex, string> = {
  1: "Setup (dokumentuppladdning och indexering)",
  2: "Extrahering (krav, kriterier, villkor, datum)",
  3: "CV-matchning (sökning och matchning av CV mot rollkrav)",
  4: "Anbudsgranskning (AI-granskning av anbudsdokument)",
  5: "Kvalificeringsanalys (kravmatris och riskanalys)",
  6: "Rekommendation (GO/NO-GO-beslutsstöd)",
}

type BuildTenderMessageParams = {
  tenderCase: TenderCase
  activeStep: StepIndex
  // Authoritative rich data from their own queries — never on tenderCase.runs
  extractionData?: TenderExtractionApi
  bidReviewData?: BidReviewResult
  cvMatchData?: CvMatchResult
  userPrompt: string
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

function docTable(docs: TenderDocument[], label: string): string {
  if (docs.length === 0) return `*Inga ${label}-dokument uppladdade.*\n`
  const rows = docs
    .map((d) => {
      const pages = d.pages > 0 ? `${d.pages} s.` : "–"
      return `| ${d.filename} | ${pages} | ${statusLabelSv(d.status)} |`
    })
    .join("\n")
  return `| Filnamn | Sidor | Status |\n|---|---|---|\n${rows}\n`
}

function docOverviewSection(
  procurementDocs: TenderDocument[],
  contextDocs: TenderDocument[],
  bidDocs: TenderDocument[]
): string {
  const lines: string[] = ["### Dokument i ärendet\n"]

  const allGroups: [TenderDocument[], string][] = [
    [procurementDocs, "Upphandling"],
    [contextDocs, "Kontext"],
    [bidDocs, "Anbud"],
  ]

  for (const [docs, label] of allGroups) {
    if (docs.length === 0) continue
    const names = docs.map((d) => d.filename).join(", ")
    lines.push(`**${label}:** ${names}`)
  }

  if (procurementDocs.length + contextDocs.length + bidDocs.length === 0) {
    lines.push("*Inga dokument uppladdade.*")
  }

  return lines.join("\n") + "\n"
}

// ── Step 1 ─────────────────────────────────────────────────────────────────────

function renderStep1(
  procurementDocs: TenderDocument[],
  contextDocs: TenderDocument[],
  bidDocs: TenderDocument[]
): string {
  const lines: string[] = []
  lines.push("### Upphandlingsdokument\n")
  lines.push(docTable(procurementDocs, "upphandlings"))
  if (contextDocs.length > 0) {
    lines.push("\n### Kontextdokument\n")
    lines.push(docTable(contextDocs, "kontext"))
  }
  if (bidDocs.length > 0) {
    lines.push("\n### Anbudsdokument\n")
    lines.push(docTable(bidDocs, "anbuds"))
  }
  return lines.join("\n")
}

// ── Step 2 ─────────────────────────────────────────────────────────────────────

function renderRequirementsSection(requirements: Requirement[]): string {
  if (requirements.length === 0) return "*Inga ska-krav extraherade.*\n"
  const lines: string[] = []
  const rows = requirements
    .map(
      (r) =>
        `| ${r.id} | ${r.description.substring(0, 60)}${r.description.length > 60 ? "…" : ""} | ${r.isMandatory ? "Obligatorisk" : "Bör-krav"} | ${r.category} | ${r.confidence} |`
    )
    .join("\n")
  lines.push(`| ID | Beskrivning | Typ | Kategori | Säkerhet |\n|---|---|---|---|---|\n${rows}\n`)
  for (const r of requirements) {
    lines.push(`\n#### ${r.id} — ${r.description.substring(0, 80)}`)
    if (r.reasoning) lines.push(`\n**Resonemang:** ${r.reasoning}`)
    if (r.snippet?.quote)
      lines.push(
        `\n**Källa:** "${r.snippet.quote}" *(${r.snippet.documentName}${pageSuffix(r.snippet.page)})*`
      )
  }
  return lines.join("\n")
}

function renderCriteriaSection(criteria: EvaluationCriterion[]): string {
  if (criteria.length === 0) return "*Inga utvärderingskriterier extraherade.*\n"
  const rows = criteria
    .map((c) => `| ${c.id} | ${c.name} | ${c.weightPercent}% | ${c.confidence} |`)
    .join("\n")
  const lines = [`| ID | Namn | Vikt | Säkerhet |\n|---|---|---|---|\n${rows}\n`]
  for (const c of criteria) {
    lines.push(`\n#### ${c.id} — ${c.name} (${c.weightPercent}%)`)
    lines.push(`\n${c.description}`)
    if (c.reasoning) lines.push(`\n**Resonemang:** ${c.reasoning}`)
    if (c.snippet?.quote)
      lines.push(
        `\n**Källa:** "${c.snippet.quote}" *(${c.snippet.documentName}${pageSuffix(c.snippet.page)})*`
      )
  }
  return lines.join("\n")
}

function renderContractTermsSection(terms: ContractTerm[]): string {
  if (terms.length === 0) return "*Inga avtalsvillkor extraherade.*\n"
  const rows = terms
    .map(
      (t) =>
        `| ${t.id} | ${t.clause} | ${statusLabelSv(t.riskLevel)} | ${t.flagged ? "⚠ Flaggad" : "–"} |`
    )
    .join("\n")
  const lines = [`| ID | Klausul | Risknivå | Flagga |\n|---|---|---|---|\n${rows}\n`]
  for (const t of terms) {
    lines.push(`\n#### ${t.id} — ${t.clause}`)
    lines.push(`\n${t.description}`)
    if (t.reasoning) lines.push(`\n**Resonemang:** ${t.reasoning}`)
    if (t.snippet?.quote)
      lines.push(
        `\n**Källa:** "${t.snippet.quote}" *(${t.snippet.documentName}${pageSuffix(t.snippet.page)})*`
      )
    if (t.frameworkDeviation) {
      lines.push(
        `\n**Ramverksavvikelse (${t.frameworkDeviation.standard}):** ${t.frameworkDeviation.deviationDescription}`
      )
    }
  }
  return lines.join("\n")
}

function renderDatesSection(dates: CriticalDate[]): string {
  if (dates.length === 0) return "*Inga tidskritiska datum extraherade.*\n"
  const rows = dates
    .map((d) => {
      const dateCol = d.date ?? d.dateText ?? "—"
      let daysCol = "—"
      if (d.daysRemaining != null) {
        daysCol = d.daysRemaining >= 0 ? `${d.daysRemaining} dagar` : "Passerat"
      }
      return `| ${d.id} | ${d.event} | ${dateCol} | ${daysCol} | ${statusLabelSv(d.urgency)} |`
    })
    .join("\n")
  return `| ID | Händelse | Datum | Dagar kvar | Prioritet |\n|---|---|---|---|---|\n${rows}\n`
}

function renderRolesSection(roles: RoleRequirement[]): string {
  if (roles.length === 0) return "*Inga rollkrav extraherade.*\n"
  const rows = roles
    .map(
      (r) =>
        `| ${r.id} | ${r.roleName} | ${r.quantity} | ${r.minYearsExperience ?? "–"} år | ${r.requiredCertifications.join(", ") || "–"} |`
    )
    .join("\n")
  const lines = [
    `| ID | Roll | Antal | Min. erfarenhet | Certifieringar |\n|---|---|---|---|---|\n${rows}\n`,
  ]
  for (const r of roles) {
    if (r.requiredExperience.length > 0 || r.meritingQualifications.length > 0 || r.reasoning) {
      lines.push(`\n#### ${r.id} — ${r.roleName}`)
      if (r.requiredExperience.length > 0)
        lines.push(`\n**Krav:** ${r.requiredExperience.join(" · ")}`)
      if (r.meritingQualifications.length > 0)
        lines.push(`\n**Meriterande:** ${r.meritingQualifications.join(" · ")}`)
      if (r.reasoning) lines.push(`\n**Resonemang:** ${r.reasoning}`)
      if (r.snippet?.quote)
        lines.push(
          `\n**Källa:** "${r.snippet.quote}" *(${r.snippet.documentName}${pageSuffix(r.snippet.page)})*`
        )
    }
  }
  return lines.join("\n")
}

function renderDeliverablesSection(deliverables: BidDeliverable[]): string {
  if (deliverables.length === 0) return "*Inga leverabler extraherade.*\n"
  const rows = deliverables
    .map((d) => `| ${d.id} | ${d.name} | ${d.isRequired ? "Obligatorisk" : "Frivillig"} |`)
    .join("\n")
  const lines = [`| ID | Leverabel | Typ |\n|---|---|---|\n${rows}\n`]
  for (const d of deliverables) {
    if (d.description || d.reasoning) {
      lines.push(`\n#### ${d.id} — ${d.name}`)
      if (d.description) lines.push(`\n${d.description}`)
      if (d.reasoning) lines.push(`\n**Resonemang:** ${d.reasoning}`)
      if (d.snippet?.quote)
        lines.push(
          `\n**Källa:** "${d.snippet.quote}" *(${d.snippet.documentName}${pageSuffix(d.snippet.page)})*`
        )
    }
  }
  return lines.join("\n")
}

function renderStep2(ext: TenderExtractionApi): string {
  const lines: string[] = []
  const effectiveSubmissionDeadline =
    ext.submissionDeadline.effectiveValue ?? ext.procurementOverview?.submissionDeadline
  const extractedSubmissionDeadline = ext.submissionDeadline.extractedValue

  if (ext.procurementOverview) {
    const o = ext.procurementOverview
    lines.push("### Upphandlingsöversikt\n")
    lines.push(o.description)
    if (o.client) lines.push(`\n- **Beställare:** ${o.client}`)
    if (o.procurementType) lines.push(`- **Typ:** ${o.procurementType}`)
    if (o.estimatedValue) lines.push(`- **Uppskattat värde:** ${o.estimatedValue}`)
    if (o.scopeSummary) lines.push(`- **Omfattning:** ${o.scopeSummary}`)
    if (o.contractDuration) lines.push(`- **Avtalstid:** ${o.contractDuration}`)
    if (effectiveSubmissionDeadline) lines.push(`- **Inlämning:** ${effectiveSubmissionDeadline}`)
    if (ext.submissionDeadline.isManual && extractedSubmissionDeadline) {
      lines.push(`- **Extraherat ursprungsvärde:** ${extractedSubmissionDeadline}`)
    }
    lines.push("\n")
  }

  lines.push("### Ska-krav\n")
  lines.push(renderRequirementsSection(ext.requirements as Requirement[]))

  lines.push("\n### Utvärderingskriterier\n")
  lines.push(renderCriteriaSection(ext.evaluationCriteria as EvaluationCriterion[]))

  lines.push("\n### Avtalsvillkor\n")
  lines.push(renderContractTermsSection(ext.contractTerms as ContractTerm[]))

  lines.push("\n### Tidskritiska datum\n")
  lines.push(renderDatesSection(ext.criticalDates as CriticalDate[]))

  lines.push("\n### Rollkrav\n")
  lines.push(renderRolesSection(ext.roleRequirements as RoleRequirement[]))

  lines.push("\n### Leverabler\n")
  lines.push(renderDeliverablesSection(ext.bidDeliverables as BidDeliverable[]))

  return lines.join("\n")
}

// ── Background summary ─────────────────────────────────────────────────────────

function renderBackground(
  run: {
    procurementDocuments: TenderDocument[]
    contextDocuments: TenderDocument[]
    bidDocuments: TenderDocument[]
  },
  activeStep: StepIndex
): string {
  if (activeStep === 1) return ""

  const docSummaryParts: string[] = []
  if (run.procurementDocuments.length > 0)
    docSummaryParts.push(
      `Upphandling: ${run.procurementDocuments.map((d) => d.filename).join(", ")}`
    )
  if (run.contextDocuments.length > 0)
    docSummaryParts.push(`Kontext: ${run.contextDocuments.map((d) => d.filename).join(", ")}`)
  if (run.bidDocuments.length > 0)
    docSummaryParts.push(`Anbud: ${run.bidDocuments.map((d) => d.filename).join(", ")}`)

  if (docSummaryParts.length === 0) return ""

  return [
    "---",
    "## Bakgrundsinformation\n",
    `**Steg 1 – Setup:** ${docSummaryParts.join(" · ")}`,
  ].join("\n")
}

// ── Entry point ────────────────────────────────────────────────────────────────

/**
 * Builds a structured Markdown user message for the tender_assistant agent.
 *
 * Rich data (requirements, bid review results, etc.) is NOT on tenderCase.runs —
 * it lives in separate queries. Pass extractionData and bidReviewData explicitly.
 */
export function buildTenderMessage({
  tenderCase,
  activeStep,
  extractionData,
  bidReviewData,
  cvMatchData,
  userPrompt,
}: BuildTenderMessageParams): string {
  const activeRun = tenderCase.runs.find((r) => r.id === tenderCase.activeRunId)

  const lines: string[] = ["<tender_context>"]

  // ── Case header
  lines.push(
    `# Anbudsärende: ${tenderCase.title}${tenderCase.reference ? ` (${tenderCase.reference})` : ""}`
  )
  lines.push("")

  const meta: string[] = [`**Status:** ${statusLabelSv(tenderCase.status)}`]
  const effectiveSubmissionDeadline =
    extractionData?.submissionDeadline.effectiveValue ?? tenderCase.submissionDeadline
  if (effectiveSubmissionDeadline) {
    meta.push(`**Inlämning:** ${effectiveSubmissionDeadline}`)
    if (extractionData?.submissionDeadline.isManual || tenderCase.submissionDeadlineIsManual) {
      meta.push("**Deadlinekälla:** Manuell")
    }
  }
  if (activeRun) meta.push(`**Aktiv körning:** Körning ${activeRun.runNumber}`)
  lines.push(meta.join(" | "))
  lines.push("")

  // ── Active step indicator
  lines.push(`## Aktiv vy: Steg ${activeStep} — ${STEP_LABELS[activeStep]}\n`)

  if (!activeRun) {
    lines.push(
      "*Ingen aktiv körning. Skapa en körning och ladda upp dokument för att komma igång.*"
    )
  } else if (activeStep === 1) {
    lines.push(
      renderStep1(
        activeRun.procurementDocuments,
        activeRun.contextDocuments,
        activeRun.bidDocuments
      )
    )
  } else {
    lines.push(
      docOverviewSection(
        activeRun.procurementDocuments,
        activeRun.contextDocuments,
        activeRun.bidDocuments
      )
    )

    if (activeStep === 2) {
      if (extractionData?.status === "partial") {
        lines.push(
          "*Vissa delar av sammanställningen behöver köras om. Färdiga resultat visas i steg 2, men används inte här förrän hela sammanställningen är klar.*"
        )
      } else if (!extractionData || extractionData.status !== "completed") {
        lines.push(
          `*Extrahering är ${statusLabelSv(extractionData?.status ?? activeRun.extractionStatus ?? "pending")}. Resultaten visas här när den är klar.*`
        )
      }
    } else if (activeStep === 3) {
      if (!cvMatchData) {
        lines.push("*CV-matchning har inte startats för den här körningen.*")
      }
    } else if (activeStep === 4) {
      if (!bidReviewData) {
        lines.push("*Ingen anbudsgranskning har startats för den här körningen.*")
      } else if (bidReviewData.status !== "completed") {
        lines.push(
          `*Anbudsgranskning är ${statusLabelSv(bidReviewData.status)}. Resultaten visas här när den är klar.*`
        )
      }
    } else {
      lines.push("*Det här steget är låst och inte tillgängligt än.*")
    }
  }

  // ── Full extraction data (always included when completed) ─────────────────
  if (activeRun && extractionData?.status === "completed") {
    lines.push("")
    lines.push("## Extraherade krav och poster (Steg 2)")
    lines.push(
      "*Använd `[[req:ID]]` (t.ex. `[[req:SKA-001]]`) för att referera till poster nedan. Om du refererar till flera poster måste varje referens stå i sin egen kompletta dubbelhakparentes, t.ex. `[[req:SKA-001]] [[req:SKA-002]]`. De renderas som klickbara referenser i chatten.*\n"
    )
    lines.push(renderStep2(extractionData))
  }

  // ── Full CV match data (always included when available) ───────────────────
  if (activeRun && cvMatchData) {
    lines.push("")
    lines.push("## CV-matchning och bemanningsplan (Steg 3)\n")
    lines.push(renderCvMatch(cvMatchData))
  }

  // ── Full bid review data (always included when completed) ─────────────────
  if (activeRun && bidReviewData?.status === "completed") {
    lines.push("")
    lines.push("## Anbudsgranskning (Steg 4)\n")
    lines.push(renderStep3(bidReviewData))
  }

  // ── Background section (doc overview when not on step 1)
  if (activeRun) {
    const bg = renderBackground(activeRun, activeStep)
    if (bg) {
      lines.push("")
      lines.push(bg)
    }
  }

  lines.push("</tender_context>")
  lines.push("")
  lines.push(userPrompt)

  return lines.join("\n")
}
